<template>
    <component
        :is="tag"
        ref="el"
        class="product-gallery"
        :aria-busy="showFillers ? 'true' : 'false'"
        :aria-live="showFillers ? 'polite' : undefined"
        @dragstart="scrollController.dragStart($event)"
        draggable="true"
        @click="clickEvent"
        :data-gallery-identifier="identifier"
    >
        <div
            v-if="
                !stringIsNullOrWhitespace(header1) ||
                !stringIsNullOrWhitespace(header2) ||
                $slots['top-part']
            "
            :class="['top-part', centerHeader ? 'centered' : '']"
        >
            <slot name="top-part"
                ><div
                    :class="['title']"
                    v-if="
                        !stringIsNullOrWhitespace(header1) ||
                        !stringIsNullOrWhitespace(header2)
                    "
                >
                    <span
                        v-if="!stringIsNullOrWhitespace(header1)"
                        class="header-1"
                        >{{ header1 }}</span
                    >
                    <template
                        v-if="
                            !stringIsNullOrWhitespace(header1) &&
                            !stringIsNullOrWhitespace(header2)
                        "
                    >
                        |
                    </template>
                    <span
                        v-if="!stringIsNullOrWhitespace(header2)"
                        class="header-2"
                        >{{ header2 }}</span
                    >
                </div></slot
            >
        </div>
        <div class="gallery-row">
            <div class="page-arrow-before">
                <div
                    class="image-wrapper"
                    @click="scrollController.scrollLeft()"
                >
                    <img
                        src="~./arrow.svg"
                        width="14"
                        height="24"
                        :alt="labels.frontendProductGallery.previous"
                    />
                </div>
            </div>
            <div class="product-gallery-slider" ref="frame">
                <ul style="position: relative">
                    <template
                        v-for="{ item, i } in items.map((item, i) => ({
                            item,
                            i,
                        }))"
                        :key="item.key"
                    >
                        <!-- <template v-if="!item.first">
                            <li
                                class="spacer"
                                :style="spacerStyle"
                                data-item-type="spacer"
                            ></li>
                        </template> -->
                        <product-gallery-item
                            ref="itemsEl"
                            :filler="item.filler"
                            :loaded="item.loaded"
                            :force-lazy-load-image="forceLazyLoadImage"
                            data-item-type="product"
                            :data-item-key="item.key"
                            :width="itemWidth"
                            :registration="item.product?.registration"
                            :product="item.product"
                            :expected-content-type="expectedContentType"
                            :visible="item.visible"
                            :index-of-list="i"
                            @mounted="itemMountEvent(item, $event, i)"
                            @unmount="itemUnmountEvent(item, $event)"
                            @dragstart="scrollController.dragStart($event)"
                        ></product-gallery-item>
                    </template>
                </ul>
            </div>
            <div class="page-arrow-after">
                <div
                    class="image-wrapper"
                    @click="scrollController.scrollRight()"
                >
                    <img
                        src="~./arrow.svg"
                        width="14"
                        height="24"
                        :alt="labels.frontendProductGallery.next"
                    />
                </div>
            </div>
        </div>
    </component>
</template>

<style scoped lang="scss">
.gallery-row {
    position: relative;
}
.top-part {
    display: flex;
    align-items: baseline;
    justify-content: space-between;

    &.centered {
        justify-content: center;
    }
}

$desktopWidth: 1110px;

@import "common.css.src/layers.scss";

@mixin arrow($side) {
    $dir: bottom;
    $alignment: center;
    @if $side == right {
        $alignment: end;
        $dir: right;
    } @else if ($side == left) {
        $alignment: start;
        $dir: left;
    }

    top: 0;
    z-index: $zIndexBody;
    height: 100%;
    align-items: center;
    display: flex;
    position: absolute;

    @if ($side == right) {
        right: 0;

        @media (min-width: $desktopWidth) {
            right: -55px;
            margin-right: -15px;
        }
    } @else if ($side == left) {
        @media (min-width: $desktopWidth) {
            left: -55px;
            margin-left: -15px;
        }
    }

    background-image: linear-gradient(to $dir, rgba(255, 255, 255, 0), #ffffff);
    @media (min-width: $desktopWidth) {
        background-image: none;
    }

    .image-wrapper {
        display: flex;
        width: 80px;

        img {
            height: 1.5rem;
            padding: 15px;
            cursor: pointer;
            align-self: center;

            @if ($side == left) {
                transform: scaleX(-1);
            }
        }

        justify-content: $alignment;
        height: 100%;

        @media (min-width: $desktopWidth) {
            display: inline-block;
            height: auto;
            width: auto;
        }
    }
}
.page-arrow-before {
    @include arrow(left);
}
.page-arrow-after {
    @include arrow(right);
}

.title {
    font-size: 20px;
    margin: 5px;
    text-transform: uppercase;

    .header-2 {
        font-weight: 900;
    }
}

// .spacer {
//     display: inline-block;
// }

.product-gallery-slider {
    width: 100%;
    overflow: hidden;
    position: relative;
    display: block;
    -ms-overflow-style: none; /* hide scrollbar for Internet Explorer, Edge */
    scrollbar-width: none; /* hide scrollbar for Firefox */
    scroll-behavior: smooth;
    overflow-x: scroll;
    overflow-y: hidden;

    > ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: block;
        flex-direction: row;
        transition: left 400ms, opacity 400ms;
        opacity: 1;
        white-space: nowrap;

        &::-webkit-scrollbar {
            display: none; /* hide scrollbar for Chrome, Safari, and Opera */
        }
    }
}
</style>

<script setup lang="ts">
import axios, { AxiosError, CanceledError } from "axios";
import {
    type PropType,
    computed,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
} from "vue";
import { isNullOrWhitespace } from "../../../shared/utils/string";
import type { ProductListCachedProduct } from "../frontend-productlist/product-list/list-item";
import { registerProduct } from "../frontend-productlist/product-list/list-item-component/common/product-updater";
import labels from "../shared/labels";
import { enqueue } from "./fetch-list-queue";
import productGalleryItem from "./ProductGalleryItem.vue";
//import { resizeToMatchUp } from "./adjust-elements-to-align";
import fastdom from "fastdom";
import { getBrowserView } from "../../../utilities/browser-view";
import { getPagePosition } from "../../../utilities/dom-measurement";
import { type Segment } from "../frontend-productlist/product-list/segment";
import { getUniqueProductGalleryIdentifier } from "./product-gallery-counter";
import { createScrollController } from "./scrolling";
import { type GalleryItem } from "./types";

const props = defineProps({
    centerHeader: Boolean,
    header1: {
        type: String,
        default: "",
    },
    header2: {
        type: String,
        default: "",
    },
    tag: {
        type: String,
        default: "div",
    },
    productsPerRow: {
        type: Number,
        default: 5,
    },
    products: {
        type: Array as PropType<Segment["products"]>,
        default: null,
    },
    fetchUrl: {
        type: String,
    },
    fetch: {
        type: Promise as PropType<Promise<Response>>,
    },
    prio: Number,
    expectedContentType: String,
    itemWidthStyle: String,
});

// const spacerWidth = ref(0);
const gallerySliderSize = ref(0);

const frame = ref<HTMLUListElement>();
const itemsEl = ref<InstanceType<typeof productGalleryItem>[]>();

const productsData = ref<Segment["products"]>([]);

const showFillers = computed(() => {
    return (
        props.products == null &&
        (props.fetchUrl != null || props.fetch != null) &&
        productsData.value.length === 0
    );
});

const identifier = ref(getUniqueProductGalleryIdentifier());

// because css wont do anything about this
// function timerResizeTitle() {
//     if (frame.value != null) {
//         resizeToMatchUp(identifier.value, frame.value, ".product-title");
//         resizeToMatchUp(identifier.value, frame.value, ".amounts");
//     }
//     requestAnimationFrame(() => {
//         setTimeout(() => timerResizeTitle(), 100);
//     });
// }

function createFiller(i: number, setFirst: boolean): GalleryItem {
    return {
        filler: true,
        key: "filler" + i,
        product: undefined,
        visible: false,
        loaded: false,
        first: setFirst && i === 0,
    };
}

function createFillers(n = 6, setFirst = true) {
    return Array.from(Array(n)).map((x, i) => createFiller(i, setFirst));
}

const items = ref<GalleryItem[]>(createFillers());

let uniqueCounter = 0;
const margin = 5 * 2;
function setItemsFromData() {
    // const pixWidth =
    //     pixelWidth.value != null ? pixelWidth.value + margin : undefined;
    // const frameWidth = frame.value?.offsetWidth;

    const result: GalleryItem[] = productsData.value.map((product, i) => {
        if (product == null || product == "filler")
            return createFiller(0, false);
        let visible = false;
        // if (pixWidth != null && frameWidth != null)
        //     visible = pixWidth * i < frameWidth;
        // else
        if (i < 5) {
            visible = true;
        }
        return {
            filler: false,
            key: "product" + product.productId + "_" + uniqueCounter++,
            product: product,
            visible: visible,
            loaded: visible,
        };
    });
    if (result.length > 0) {
        result[0].first = true;
    }

    for (const item of result) {
        if (item.filler) continue;
        if (!item.product) continue;
        if (item.product.registration) continue;
        item.product.registration = registerProduct(
            reactive({
                props: {
                    isShop: item.product.isShop,
                    productId: item.product.productId,
                    secondsLeft: item.product.timeTotalSeconds,
                },
            })
        );
    }

    items.value = result;
}

const itemWidth = computed(() => {
    if (props.itemWidthStyle != null) return props.itemWidthStyle;
    return "230px";
});

const pixelWidth = computed(() => {
    return itemWidth.value != null && itemWidth.value.endsWith("px")
        ? parseInt(itemWidth.value.substring(0, itemWidth.value.length - 2))
        : undefined;
});

// const spacerStyle = computed(() => {
//     const style: { [key: string]: string } = {};
//     style["width"] = spacerWidth.value + "px";
//     return style;
// });

const el = ref<HTMLElement>();

function stringIsNullOrWhitespace(s: string | null | undefined) {
    return isNullOrWhitespace(s);
}
function resizeEventHandler() {
    updateSizing();
}
function updateSizing() {
    setSizeForGallerySlider();
}
function setSizeForGallerySlider() {
    let width = gallerySliderSize.value;

    if (frame.value == null) return;

    const frameBoundRect = frame.value.getBoundingClientRect();

    width = frameBoundRect.width;

    gallerySliderSize.value = width;

    //console.log("NEW SIZE", width);
}
async function fetchFromUrl() {
    let responseData: {
        products: ProductListCachedProduct[];
        productsPerRow: number;
    };
    if (props.fetch != null) {
        responseData = (await (
            await props.fetch
        ).json()) as typeof responseData;
    } else if (props.fetchUrl != null) {
        try {
            const response = await axios.get<typeof responseData>(
                props.fetchUrl
            );
            responseData = response.data;
        } catch (e) {
            if (e instanceof CanceledError) {
                responseData = { products: [], productsPerRow: 5 };
                return;
            }
            throw e;
        }
    } else throw new Error("No fetchUrl");
    productsData.value = responseData.products;
    setItemsFromData();
}

onBeforeMount(() => {
    if (props.products != null) {
        productsData.value = props.products;
        setItemsFromData();
    } else if (
        (props.fetchUrl != null && props.fetchUrl != "") ||
        props.fetch != null
    ) {
        productsData.value = [];
        items.value = createFillers();
        enqueue(props.prio ?? 0, fetchFromUrl);
    }
});

const interscetionCallback: IntersectionObserverCallback = (entries) => {
    entries.forEach((x) => {
        if (x.target.getAttribute("data-item-type") !== "product") {
            return;
        }
        const key = x.target.getAttribute("data-item-key");
        const match = items.value.find((x) => x.key === key);
        if (match == null) {
            return;
        }
        match.intersection = x.intersectionRatio;
        match.visible = x.intersectionRatio > 0;
        if (match.visible) {
            match.loaded = true;
        }
    });
};

const scrollController = createScrollController(frame, items, el);

// setInterval(() => {
//     const itemsElValue = itemsEl.value;
//     if (itemsElValue == null) return;
//     for (const item of itemsElValue) {
//         item.$el.style.backgroundColor = "";
//     }
//     const result = getFirstAndLastFullyVisibleItem(itemsEl);
//     if (result.firstFullyVisible)
//         result.firstFullyVisible.el.style.backgroundColor = "green";
//     if (result.lastFullyVisible)
//         result.lastFullyVisible.el.style.backgroundColor = "red";
// }, 1000);

const intersectionObserver = ref<IntersectionObserver>();
const firstItemResizeObserver = ref<ResizeObserver>();

const firstItemWidth = ref<number>();

const observeQueue: HTMLElement[] = [];
const firstItemObserveQueue: HTMLElement[] = [];

const browserView = getBrowserView();
const forceLazyLoadImage = ref(true);

onMounted(async () => {
    //timerResizeTitle();

    if (el.value) {
        const rootEl = el.value;
        fastdom.measure(() => {
            const elOffset = getPagePosition(rootEl);

            const browserPos = browserView.value;
            if (
                elOffset.top < browserPos.bottom &&
                elOffset.bottom > browserPos.top
            ) {
                forceLazyLoadImage.value = false;
                //console.log("gallery in view");
            } else {
                //console.log("gallery outside view");
            }
        });
    }

    window.addEventListener("resize", () => {
        resizeEventHandler();
    });

    const options: IntersectionObserverInit = {
        root: frame.value,
        rootMargin: "0px",
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
    };

    intersectionObserver.value = new IntersectionObserver(
        interscetionCallback,
        options
    );
    for (const observeItem of observeQueue) {
        let el = observeItem;
        if ("$el" in el) {
            el = el.$el as HTMLElement;
        }
        intersectionObserver.value.observe(el);
    }

    firstItemResizeObserver.value = new ResizeObserver((entries) => {
        firstItemWidth.value = entries[0].contentRect.width;
    });

    for (const observeItem of firstItemObserveQueue) {
        let el = observeItem;
        if ("$el" in el) {
            el = el.$el as HTMLElement;
        }
        firstItemResizeObserver.value.observe(el);
    }
});

onBeforeUnmount(() => {
    intersectionObserver.value?.disconnect();
});

function itemMountEvent(item: GalleryItem, el: HTMLElement, index: number) {
    if (!item.filler) {
        observeItemEl(el);
    }
    if (index == 0) {
        observeFirstItemResize(el);
    }
}

function observeFirstItemResize(el: HTMLElement) {
    if (firstItemResizeObserver.value == null) {
        firstItemObserveQueue.push(el);
        return;
    }
    if ("$el" in el) {
        el = el.$el as HTMLElement;
    }
    firstItemResizeObserver.value.observe(el);
}

function observeItemEl(el: HTMLElement) {
    if (intersectionObserver.value == null) {
        observeQueue.push(el);
        return;
    }
    if (el === undefined) {
        console.warn("el is undefined. cannot observe.");
        return;
    }
    if ("$el" in el) {
        el = el.$el as HTMLElement;
    }
    if (!(el instanceof Element)) {
        //console.log(el);
        //debugger;
    }
    intersectionObserver.value.observe(el);
}
function itemUnmountEvent(item: GalleryItem, el: HTMLElement) {
    //if (item.filler) return;
    //console.log("UNOBSERVING");
    unobserveItemEl(el);
}
function unobserveItemEl(el: HTMLElement) {
    if (intersectionObserver.value == null) {
        const index = observeQueue.indexOf(el);
        if (index >= 0) {
            observeQueue.splice(index, 1);
        }
        return;
    }
    if ("$el" in el) {
        el = el.$el as HTMLElement;
    }
    intersectionObserver.value.unobserve(el);
}

function clickEvent(ev: Event) {
    //console.log("clickEvent");
}

// function getParentLink(el: EventTarget | Element | null) {
//     if (el == null) return undefined;
//     if (!(el instanceof Element)) return undefined;
//     if (el.tagName === "A") return el;
//     return getParentLink(el.parentElement);
// }
// let lastLog = "";
// const interval = setInterval(() => {
//     let logTxt =
//         "gallery items: " +
//         items.value?.length +
//         " frameheight" +
//         frame.value?.offsetHeight +
//         " firstFrameHeight" +
//         (frame.value?.children[0] as HTMLElement | undefined)?.offsetHeight +
//         " firstFrameSourceLen" +
//         (frame.value?.children[0] as HTMLElement | undefined)?.innerHTML.length;
//     if (
//         ((frame.value?.children[0] as HTMLElement | undefined)?.innerHTML
//             .length ?? 999999) < 2000
//     ) {
//         logTxt +=
//             " firstFrameSource: " +
//             (frame.value?.children[0] as HTMLElement | undefined)?.outerHTML;
//     }
//     if (logTxt == lastLog) return;
//     console.log(logTxt);
//     lastLog = logTxt;
// }, 10);
// onBeforeUnmount(() => {
//     clearInterval(interval);
// });
</script>
